"use client";

import { cn } from "@/lib/utils";
import Link from "@app/_components/Link";
import { useMarket } from "@features/market/useMarket";
import useTranslate from "@app/_hooks/useTranslate";
import { Button } from "@ui/button";
import { Field, FieldError, FieldLabel, FieldSet } from "@ui/field";
import { Heading } from "@ui/heading";
import { Paragraph } from "@ui/paragraph";
import { Loader2 } from "lucide-react";
import type { ComponentProps, ElementType } from "react";
import usePasswordResetForm from "../hooks/usePasswordResetForm";
import getFieldErrorState from "../utils/getFieldErrorState";

export default function ForgottenPasswordForm({
  className,
  header: HeaderComponent = Heading,
  description: DescriptionComponent = Paragraph,
  callback,
  ...props
}: ComponentProps<"div"> & {
  header?: ElementType;
  description?: ElementType;
  callback: () => void;
}) {
  const t = useTranslate();
  const market = useMarket();
  const form = usePasswordResetForm();

  return (
    <div className={cn("grid grid-flow-row gap-4", className)} {...props}>
      <form.Subscribe
        selector={(state) => state.isSubmitSuccessful}
        children={(isSubmitSuccessful) =>
          isSubmitSuccessful ? (
            <>
              <HeaderComponent className="sr-only">
                {t("forgotPassword")}
              </HeaderComponent>
              <DescriptionComponent className="sr-only">
                {t("passwordResetInstructions")}
              </DescriptionComponent>
              <Paragraph className="text-center" role="status">
                {t("passwordResetSuccess")}
              </Paragraph>
              <Button type="button" onClick={callback} className="w-full">
                {t("backToLogin")}
              </Button>
            </>
          ) : (
            <>
              <HeaderComponent className="text-2xl">
                {t("forgotPassword")}
              </HeaderComponent>
              <DescriptionComponent>
                {t("passwordResetInstructions")}
              </DescriptionComponent>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  form.handleSubmit();
                }}
                className="border-b"
              >
                <FieldSet>
                  <form.AppField
                    name="email"
                    children={(field) => (
                      <Field
                        invalid={
                          field.getMeta().isDirty && !field.getMeta().isValid
                        }
                        dirty={field.getMeta().isDirty}
                        touched={field.getMeta().isTouched}
                      >
                        <FieldLabel placeholder>{t("email")}</FieldLabel>
                        <field.Input
                          autoComplete="email"
                          placeholder={t("email")}
                          type="email"
                          value={field.state.value}
                          onChange={(event) =>
                            field.handleChange(event.target.value)
                          }
                          onBlur={() => field.handleBlur()}
                        />
                        {getFieldErrorState(field) && (
                          <FieldError>
                            {t(field.getMeta().errors[0]!)}
                          </FieldError>
                        )}
                      </Field>
                    )}
                  />
                  <form.Subscribe
                    selector={(state) => [
                      state.isValid,
                      state.isDirty,
                      state.isSubmitting,
                    ]}
                    children={([isValid, isDirty, isSubmitting]) => (
                      <Button
                        className="w-full"
                        type="submit"
                        disabled={!isDirty || !isValid || isSubmitting}
                      >
                        {isSubmitting ? (
                          <Loader2
                            className="size-5 animate-spin"
                            strokeWidth={2}
                          />
                        ) : (
                          t("send")
                        )}
                      </Button>
                    )}
                  />
                </FieldSet>
                <form.Subscribe
                  selector={(state) => state.errors}
                  children={(errors) =>
                    errors.map((error) => (
                      <form.FormError
                        key={error as string}
                        type="error"
                        className="mt-4"
                      >
                        {t(error as string)}
                      </form.FormError>
                    ))
                  }
                />
                <Button
                  type="button"
                  onClick={callback}
                  variant="link"
                  className="w-full"
                >
                  {t("backToLogin")}
                </Button>
              </form>
            </>
          )
        }
      />
      <Paragraph className="text-center">
        {t("noAccount")}
        <Link
          locale={market.siteLocale}
          href="/register"
          className="all-small-caps block tracking-wider uppercase underline underline-offset-4"
          replace
        >
          {t("register")}
        </Link>
      </Paragraph>
    </div>
  );
}
